import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentGameTimestamp } from '../config/dateTimeConfig';

const STORAGE_KEY = 'wallstreetle_overall_streak';
const STORAGE_VERSION = 1;

interface OverallStreakData {
  currentStreak: number;
  lastWinDate: string | null; // YYYY-MM-DD format
  maxStreak: number;
  version: number;
  lastSyncedAt?: string; // ISO timestamp of last Supabase sync
}

const createInitialStreakData = (): OverallStreakData => ({
  currentStreak: 0,
  lastWinDate: null,
  maxStreak: 0,
  version: STORAGE_VERSION,
});

/**
 * Load overall streak data from localStorage
 */
const loadStreakData = (): OverallStreakData => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      return createInitialStreakData();
    }

    const parsed = JSON.parse(saved) as OverallStreakData;

    if (parsed.version !== STORAGE_VERSION) {
      return {
        ...createInitialStreakData(),
        currentStreak: parsed.currentStreak || 0,
        lastWinDate: parsed.lastWinDate || null,
        maxStreak: parsed.maxStreak || 0,
      };
    }

    return parsed;
  } catch (error) {
    console.error('Error loading overall streak data:', error);
    return createInitialStreakData();
  }
};

/**
 * Save overall streak data to localStorage
 */
const saveStreakData = (data: OverallStreakData): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving overall streak data:', error);
  }
};

type StreakListener = () => void;
const streakListeners = new Set<StreakListener>();
let streakStore: OverallStreakData | null = null;

const getStreakStore = (): OverallStreakData => {
  if (!streakStore) {
    streakStore = loadStreakData();
  }
  return streakStore;
};

const isSameStreakData = (a: OverallStreakData, b: OverallStreakData) =>
  a.currentStreak === b.currentStreak &&
  a.lastWinDate === b.lastWinDate &&
  a.maxStreak === b.maxStreak &&
  a.version === b.version;

const setStreakStore = (updater: (prev: OverallStreakData) => OverallStreakData): OverallStreakData => {
  const previous = getStreakStore();
  const next = updater(previous);

  if (isSameStreakData(previous, next)) {
    return previous;
  }

  streakStore = next;
  saveStreakData(next);
  streakListeners.forEach(listener => listener());
  return next;
};

const subscribeToStreakStore = (listener: StreakListener) => {
  streakListeners.add(listener);
  return () => {
    streakListeners.delete(listener);
  };
};

const syncStoreFromStorage = () => {
  streakStore = loadStreakData();
  streakListeners.forEach(listener => listener());
};

/**
 * Calculate the new streak based on the last win date (guest users only)
 */
const calculateNewStreak = (lastWinDate: string | null, currentStreak: number, gameDate: string): number => {
  if (!lastWinDate) {
    return 1;
  }

  if (lastWinDate === gameDate) {
    return currentStreak;
  }

  const lastWin = new Date(lastWinDate + 'T00:00:00Z');
  const today = new Date(gameDate + 'T00:00:00Z');

  const diffMs = today.getTime() - lastWin.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 1) {
    return currentStreak + 1;
  } else if (diffDays > 1) {
    return 1;
  } else {
    return currentStreak;
  }
};

/**
 * Fetch streak from Supabase RPC (source of truth for authenticated users)
 */
const fetchStreakFromRPC = async (userId: string): Promise<{ current_streak: number; max_streak: number } | null> => {
  try {
    const gameDate = getCurrentGameTimestamp();
    const { data, error } = await supabase
      .rpc('calculate_overall_streak', {
        p_user_id: userId,
        p_game_date: gameDate,
      });

    if (error) {
      console.error('Error fetching streak from RPC:', error);
      return null;
    }

    if (data && data.length > 0) {
      return data[0];
    }

    return { current_streak: 0, max_streak: 0 };
  } catch (error) {
    console.error('Failed to fetch streak from RPC:', error);
    return null;
  }
};

/**
 * Upsert overall streak to Supabase for backwards compat cache
 */
const upsertStreakToSupabase = async (
  userId: string,
  streakData: OverallStreakData
): Promise<void> => {
  try {
    await supabase
      .from('overall_win_streak')
      .upsert({
        user_id: userId,
        win_streak: streakData.currentStreak,
        last_played_date: streakData.lastWinDate,
        max_streak: streakData.maxStreak
      }, {
        onConflict: 'user_id'
      });
  } catch (error) {
    console.error('Failed to upsert streak to Supabase:', error);
  }
};

/**
 * Custom hook for managing overall streak across all categories
 * Tracks consecutive days where the user plays at least one category (any outcome)
 * For logged-in users: derives streak from game_plays via RPC (source of truth)
 * For anonymous users: uses localStorage only
 */
export function useOverallStreak() {
  const [streakData, setStreakData] = useState<OverallStreakData>(() => getStreakStore());
  const { user } = useAuth();
  const [isSyncing, setIsSyncing] = useState(false);

  // Subscribe to store updates so all hook instances stay in sync immediately
  useEffect(() => {
    const unsubscribe = subscribeToStreakStore(() => setStreakData(getStreakStore()));
    return unsubscribe;
  }, []);

  // Sync from other tabs when localStorage changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        syncStoreFromStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // For authenticated users: fetch accurate streak from RPC on mount / login
  useEffect(() => {
    if (!user) return;

    const syncFromRPC = async () => {
      setIsSyncing(true);
      const rpcResult = await fetchStreakFromRPC(user.id);

      if (rpcResult) {
        const gameDate = getCurrentGameTimestamp();
        const newData: OverallStreakData = {
          currentStreak: rpcResult.current_streak,
          lastWinDate: rpcResult.current_streak > 0 ? gameDate : null,
          maxStreak: rpcResult.max_streak,
          version: STORAGE_VERSION,
          lastSyncedAt: new Date().toISOString(),
        };

        setStreakStore(() => newData);

        // Update the cache table for backwards compat
        upsertStreakToSupabase(user.id, newData);
      }

      setIsSyncing(false);
    };

    syncFromRPC();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /**
   * Record a play (any outcome) for a game date
   * For logged-in users: optimistic localStorage update, then delayed RPC refetch
   * For anonymous users: updates localStorage only
   */
  const recordPlay = useCallback((gameDate: string) => {
    if (user) {
      // Authenticated: optimistic update from localStorage, then correct via RPC
      const newData = setStreakStore((prev) => {
        const newStreak = calculateNewStreak(prev.lastWinDate, prev.currentStreak, gameDate);
        const newMaxStreak = Math.max(prev.maxStreak, newStreak);

        return {
          currentStreak: newStreak,
          lastWinDate: gameDate,
          maxStreak: newMaxStreak,
          version: STORAGE_VERSION,
          lastSyncedAt: new Date().toISOString(),
        };
      });

      // Update the cache table
      upsertStreakToSupabase(user.id, newData);

      // Delayed RPC refetch to correct any drift after game_plays insert propagates
      setTimeout(async () => {
        const rpcResult = await fetchStreakFromRPC(user.id);
        if (rpcResult) {
          const correctedData: OverallStreakData = {
            currentStreak: rpcResult.current_streak,
            lastWinDate: rpcResult.current_streak > 0 ? gameDate : null,
            maxStreak: rpcResult.max_streak,
            version: STORAGE_VERSION,
            lastSyncedAt: new Date().toISOString(),
          };

          setStreakStore(() => correctedData);
          upsertStreakToSupabase(user.id, correctedData);
        }
      }, 800);
    } else {
      // Guest: localStorage only (unchanged behavior)
      setStreakStore((prev) => {
        const newStreak = calculateNewStreak(prev.lastWinDate, prev.currentStreak, gameDate);
        const newMaxStreak = Math.max(prev.maxStreak, newStreak);

        return {
          currentStreak: newStreak,
          lastWinDate: gameDate,
          maxStreak: newMaxStreak,
          version: STORAGE_VERSION,
          lastSyncedAt: new Date().toISOString(),
        };
      });
    }
  }, [user]);

  /**
   * Reset the streak (for testing or manual reset)
   */
  const resetStreak = useCallback(() => {
    setStreakStore(() => createInitialStreakData());
  }, []);

  const currentStreak = streakData.currentStreak;
  const maxStreak = streakData.maxStreak;
  const lastWinDate = streakData.lastWinDate;

  return {
    currentStreak,
    maxStreak,
    lastWinDate,
    recordPlay,
    resetStreak,
    isSyncing,
  };
}

export default useOverallStreak;
